import React from 'react';
import { Section } from '../layout/Section';
import { Icon } from '../ui/Icon';
import contentData from '../../data/content.json';
import type { ContentData } from '../../types/content';

const content = contentData as ContentData;

interface SocialCardProps {
  platform: 'instagram' | 'facebook';
  url: string;
  label: string;
  description: string;
  color: string;
  hoverColor: string;
}

/**
 * Individual social media card component
 */
const SocialCard: React.FC<SocialCardProps> = ({
  platform,
  url,
  label,
  description,
  color,
  hoverColor,
}) => {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`group flex flex-col items-center gap-5 p-8 rounded-xl bg-[#242424] border border-transparent hover:border-[#c9a84c]/30 transition-all duration-300 hover:bg-[#2a2a2a] focus:outline-none focus:ring-2 focus:ring-[#c9a84c] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]`}
      aria-label={`${label} (öffnet in neuem Tab)`}
    >
      {/* Icon */}
      <div
        className={`w-20 h-20 rounded-full flex items-center justify-center transition-colors duration-300 ${color} group-hover:${hoverColor}`}
      >
        <Icon
          name={platform}
          size={40}
          className="text-white"
          decorative
        />
      </div>

      {/* Platform name */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#c9a84c] transition-colors duration-300">
          {label}
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
          {description}
        </p>
      </div>

      {/* Follow indicator */}
      <span className="text-[#c9a84c] text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        Folgen →
      </span>
    </a>
  );
};

/**
 * SocialSection Component
 * 
 * Displays social media links for Instagram and Facebook.
 * Shows a placeholder message if URLs are not configured.
 * 
 * Requirements:
 * - 6.1: Display Instagram link
 * - 6.2: Display Facebook link
 * - 6.3: Open links in new browser tab
 * - 6.4: Use social media icons
 * - 15.1: Accessible icon labels
 * - 15.2: Proper ARIA attributes
 */
export const SocialSection: React.FC = () => {
  const { instagram, facebook } = content.social;

  const hasInstagram = Boolean(instagram);
  const hasFacebook = Boolean(facebook);
  const hasAnySocial = hasInstagram || hasFacebook;

  return (
    <Section
      id="social"
      variant="darker"
      aria-labelledby="social-heading"
    >
      {/* Section Header */}
      <div className="text-center mb-12">
        <h2
          id="social-heading"
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
        >
          Folgen Sie uns
        </h2>
        <div className="w-24 h-1 bg-[#c9a84c] mx-auto rounded-full" aria-hidden="true" />
        <p className="mt-4 text-gray-400 text-lg">
          Bleiben Sie auf dem Laufenden
        </p>
      </div>

      {hasAnySocial ? (
        /* Social Media Cards */
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {hasInstagram && (
            <SocialCard
              platform="instagram"
              url={instagram}
              label="Instagram"
              description="Folgen Sie uns auf Instagram für aktuelle Fotos und Neuigkeiten aus dem Restaurant Fey."
              color="bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400"
              hoverColor="bg-gradient-to-br from-purple-500 via-pink-400 to-orange-300"
            />
          )}
          {hasFacebook && (
            <SocialCard
              platform="facebook"
              url={facebook}
              label="Facebook"
              description="Besuchen Sie unsere Facebook-Seite für Events, Angebote und Neuigkeiten."
              color="bg-[#1877F2]"
              hoverColor="bg-[#166FE5]"
            />
          )}
        </div>
      ) : (
        /* Placeholder when URLs not configured */
        <div className="max-w-2xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Instagram placeholder */}
            <div className="flex flex-col items-center gap-5 p-8 rounded-xl bg-[#242424] border border-dashed border-gray-600">
              <div className="w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-br from-purple-600/40 via-pink-500/40 to-orange-400/40">
                <Icon
                  name="instagram"
                  size={40}
                  className="text-gray-500"
                  decorative
                />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-500 mb-2">Instagram</h3>
                <p className="text-gray-600 text-sm">Bald verfügbar</p>
              </div>
            </div>

            {/* Facebook placeholder */}
            <div className="flex flex-col items-center gap-5 p-8 rounded-xl bg-[#242424] border border-dashed border-gray-600">
              <div className="w-20 h-20 rounded-full flex items-center justify-center bg-[#1877F2]/20">
                <Icon
                  name="facebook"
                  size={40}
                  className="text-gray-500"
                  decorative
                />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-500 mb-2">Facebook</h3>
                <p className="text-gray-600 text-sm">Bald verfügbar</p>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-500 text-sm mt-6">
            Unsere Social-Media-Profile werden in Kürze verfügbar sein.
          </p>
        </div>
      )}
    </Section>
  );
};
